import { useCallback, useEffect, useState } from "react";
import { format, addMonths } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const DOTS = { working: "bg-[#2C5545]", on_call: "bg-[#8B5CF6]", off: "bg-slate-300" };

export default function MonthView() {
  const [month, setMonth] = useState(() => new Date());
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get(`/schedule/month?month=${format(month, "yyyy-MM")}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load month"));
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const today = format(new Date(), "yyyy-MM-dd");
  const monthKey = format(month, "yyyy-MM");

  return (
    <div data-testid="month-view">
      <div className="mb-4 flex items-center gap-2">
        <h2 className="mr-auto font-heading text-xl font-semibold text-[#1A1A1A]" data-testid="month-label">{format(month, "MMMM yyyy")}</h2>
        <Button variant="outline" size="icon" className="rounded-full bg-white/70" onClick={() => setMonth(addMonths(month, -1))} data-testid="prev-month-btn"><ChevronLeft className="h-4 w-4" /></Button>
        <Button variant="outline" className="rounded-full bg-white/70" onClick={() => setMonth(new Date())} data-testid="this-month-btn">This Month</Button>
        <Button variant="outline" size="icon" className="rounded-full bg-white/70" onClick={() => setMonth(addMonths(month, 1))} data-testid="next-month-btn"><ChevronRight className="h-4 w-4" /></Button>
      </div>

      {!data ? (
        <p className="py-16 text-center text-sm text-slate-400">Loading…</p>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-white/40 bg-white/70 shadow-[0_8px_30px_rgb(0,0,0,0.04)] backdrop-blur-2xl">
          <div className="grid grid-cols-7 border-b border-slate-200/70 bg-white/50">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
              <div key={d} className="p-2.5 text-center text-xs font-bold uppercase tracking-[0.2em] text-[#5C6661]">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {data.dates.map((dateStr) => {
              const inMonth = dateStr.startsWith(monthKey);
              let working = 0, onCall = 0, events = 0, visitCount = 0;
              for (const emp of data.employees) {
                const ent = data.entries?.[emp.id]?.[dateStr];
                const wd = new Date(dateStr + "T12:00:00").getDay();
                const shift = ent ? ent.shift : (wd >= 1 && wd <= 5 ? "working" : "off");
                if (shift === "working") working++;
                if (shift === "on_call") onCall++;
                events += (data.timeoff?.[emp.id]?.[dateStr] || []).length;
                visitCount += (data.visits?.[emp.id]?.[dateStr] || []).length;
              }
              return (
                <div
                  key={dateStr}
                  className={`min-h-[96px] border-b border-r border-slate-100 p-2 [&:nth-child(7n)]:border-r-0 ${inMonth ? "bg-transparent" : "bg-slate-50/40"} ${dateStr === today ? "bg-[#2C5545]/5" : ""}`}
                  data-testid={`calendar-day-${dateStr}`}
                >
                  <p className={`text-xs font-bold ${dateStr === today ? "flex h-6 w-6 items-center justify-center rounded-full bg-[#2C5545] text-white" : inMonth ? "text-[#1A1A1A]" : "text-slate-300"}`}>
                    {Number(dateStr.slice(8))}
                  </p>
                  <div className="mt-1.5 space-y-1">
                    {working > 0 && (
                      <div className="flex items-center gap-1.5 text-[10px] font-semibold text-[#1E3B30]">
                        <span className={`h-1.5 w-1.5 rounded-full ${DOTS.working}`} />{working} working
                      </div>
                    )}
                    {onCall > 0 && (
                      <div className="flex items-center gap-1.5 text-[10px] font-semibold text-[#5B21B6]">
                        <span className={`h-1.5 w-1.5 rounded-full ${DOTS.on_call}`} />{onCall} on call
                      </div>
                    )}
                    {events > 0 && (
                      <div className="flex items-center gap-1.5 text-[10px] font-semibold text-[#9F1239]">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#F43F5E]" />{events} time-off
                      </div>
                    )}
                    {visitCount > 0 && (
                      <div className="flex items-center gap-1.5 text-[10px] font-semibold text-[#8C5A20]">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#F59E0B]" />{visitCount} visit{visitCount > 1 ? "s" : ""}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-4 text-xs font-medium text-[#5C6661]" data-testid="month-legend">
        <span className="flex items-center gap-1.5"><span className={`h-2 w-2 rounded-full ${DOTS.working}`} /> Working</span>
        <span className="flex items-center gap-1.5"><span className={`h-2 w-2 rounded-full ${DOTS.on_call}`} /> On Call</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#F43F5E]" /> Time Off</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#F59E0B]" /> Patient Visits</span>
      </div>
    </div>
  );
}
